import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { formatDetail } from "@/utils/formatDetail";
import { Loader2, Plus, Trash2, X, ShieldCheck, User, Users, Check, Pencil } from "lucide-react";

const GROUP_LABELS = {
  enquiries: "ENQUIRIES",
  customers: "CUSTOMERS",
  quotations: "QUOTATIONS",
  orders: "ORDERS",
  bills: "SALE BILLS",
  statements: "STATEMENTS",
  ledger: "LEDGER",
  dashboard: "DASHBOARD",
  calculator: "CALCULATOR",
  rates: "RATES / SETTINGS",
  team: "TEAM & PERMISSIONS",
};
const ACTION_LABELS = {
  view: "View", create: "Create", edit: "Edit", delete: "Delete",
  email: "Email", revise: "Revise", milestones: "Update Milestones",
};
const GROUP_ORDER = ["enquiries", "customers", "quotations", "orders", "bills", "statements", "ledger", "dashboard", "calculator", "rates", "team"];

const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleDateString("en-IN");
  } catch {
    return s;
  }
};

export const TeamSetup = ({ client, api }) => {
  const [groups, setGroups] = useState({});
  const [teams, setTeams] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamModal, setTeamModal] = useState(null); // {id?, name, permissions[]}
  const [empModal, setEmpModal] = useState(false);
  const [empForm, setEmpForm] = useState({ name: "", email: "", password: "", team_id: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [g, t, m] = await Promise.all([
        client.get(`${api}/permission-groups`),
        client.get(`${api}/teams`),
        client.get(`${api}/team`),
      ]);
      setGroups(g.data);
      setTeams(t.data);
      setMembers(m.data);
    } catch {
      toast.error("Could not load team data");
    } finally {
      setLoading(false);
    }
  }, [client, api]);

  useEffect(() => {
    load();
  }, [load]);

  const allPerms = Object.entries(groups).flatMap(([g, acts]) => acts.map((a) => `${g}:${a}`));
  const selected = teamModal?.permissions || [];

  const togglePerm = (p) => {
    const next = selected.includes(p) ? selected.filter((x) => x !== p) : [...selected, p];
    setTeamModal({ ...teamModal, permissions: next });
  };

  const toggleGroup = (g) => {
    const groupPerms = (groups[g] || []).map((a) => `${g}:${a}`);
    const allOn = groupPerms.every((p) => selected.includes(p));
    const next = allOn ? selected.filter((p) => !groupPerms.includes(p)) : [...new Set([...selected, ...groupPerms])];
    setTeamModal({ ...teamModal, permissions: next });
  };

  const saveTeam = async () => {
    if (!teamModal.name.trim()) return toast.error("Enter a team name");
    setSaving(true);
    try {
      if (teamModal.id) {
        await client.put(`${api}/teams/${teamModal.id}`, { name: teamModal.name, permissions: teamModal.permissions });
        toast.success("Team updated");
      } else {
        await client.post(`${api}/teams`, { name: teamModal.name, permissions: teamModal.permissions });
        toast.success("Team created");
      }
      setTeamModal(null);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail, "Could not save team"));
    } finally {
      setSaving(false);
    }
  };

  const removeTeam = async (t) => {
    if (!window.confirm(`Delete team "${t.name}"? Members fall back to default sales access.`)) return;
    try {
      await client.delete(`${api}/teams/${t.id}`);
      toast.success("Team removed");
      load();
    } catch {
      toast.error("Could not delete team");
    }
  };

  const saveEmployee = async () => {
    if (!empForm.name.trim() || !empForm.email.trim() || !empForm.password) return toast.error("Fill name, email and password");
    setSaving(true);
    try {
      await client.post(`${api}/team`, { ...empForm, team_id: empForm.team_id || null });
      toast.success("Employee added");
      setEmpModal(false);
      setEmpForm({ name: "", email: "", password: "", team_id: "" });
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail, "Could not add employee"));
    } finally {
      setSaving(false);
    }
  };

  const assignTeam = async (m, team_id) => {
    try {
      await client.put(`${api}/team/${m.id}`, { team_id: team_id || null });
      toast.success("Team assignment updated");
      load();
    } catch {
      toast.error("Could not update assignment");
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name}? Their login stops working immediately.`)) return;
    try {
      await client.delete(`${api}/team/${m.id}`);
      toast.success("Employee removed");
      load();
    } catch {
      toast.error("Could not remove employee");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24" data-testid="team-loading">
        <Loader2 size={32} className="animate-spin text-smoke" />
      </div>
    );
  }

  return (
    <div data-testid="team-setup">
      <div className="flex justify-end mb-6">
        <button data-testid="add-employee-button" onClick={() => setEmpModal(true)} className="btn-sweep btn-navy font-heading font-semibold uppercase tracking-wider text-sm px-5 py-2.5 flex items-center gap-2">
          <Plus size={15} /> Add Employee
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mb-6">
        <div className="bg-steel border border-white/10 p-6">
          <p className="flex items-center gap-2 font-heading font-semibold uppercase tracking-wider text-white mb-3"><ShieldCheck size={17} className="text-[#7FB3E8]" /> Admin</p>
          <ul className="font-body text-sm text-smoke flex flex-col gap-1.5">
            <li>· Full access — always sees every tab and action</li>
            <li>· Sets sheet rate, overhead %, margin % and GST %</li>
            <li>· Manages employees, teams and permissions</li>
            <li>· Sees cost, overhead &amp; margin on quotations</li>
          </ul>
        </div>
        <div className="bg-steel border border-white/10 p-6">
          <p className="flex items-center gap-2 font-heading font-semibold uppercase tracking-wider text-white mb-3"><User size={17} className="text-[#7FB3E8]" /> Sales</p>
          <ul className="font-body text-sm text-smoke flex flex-col gap-1.5">
            <li>· Access is limited to the team assigned to them</li>
            <li>· Never sees cost / margin (kept confidential)</li>
            <li>· If no team assigned, falls back to default sales access</li>
            <li>· Cannot change rates or delete records</li>
          </ul>
        </div>
      </div>

      <div className="bg-steel border border-white/10 mb-6">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <p className="flex items-center gap-2 font-heading font-semibold uppercase tracking-wider text-white"><Users size={16} className="text-[#7FB3E8]" /> Teams ({teams.length})</p>
          <button data-testid="new-team-button" onClick={() => setTeamModal({ name: "", permissions: [] })} className="btn-sweep btn-ghost font-heading font-semibold uppercase tracking-wider text-sm px-4 py-2 flex items-center gap-2">
            <Plus size={14} /> New Team
          </button>
        </div>
        {teams.length === 0 ? (
          <p data-testid="teams-empty" className="p-8 text-center font-body text-sm text-smoke">No teams yet. Click + New Team to create one and pick tab-wise permissions.</p>
        ) : (
          <div data-testid="teams-list">
            {teams.map((t) => (
              <div key={t.id} data-testid={`team-row-${t.id.slice(0, 8)}`} className="flex items-center justify-between px-5 py-4 border-b border-white/10 last:border-0">
                <div>
                  <p className="font-heading font-semibold uppercase tracking-wide text-white">{t.name}</p>
                  <p className="font-mono text-xs text-smoke mt-0.5">{t.permissions.length} / {allPerms.length} permissions enabled</p>
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid={`team-edit-${t.id.slice(0, 8)}`} onClick={() => setTeamModal({ id: t.id, name: t.name, permissions: t.permissions })} aria-label="Edit team" className="p-2 text-white/60 hover:text-white transition-colors duration-300"><Pencil size={15} /></button>
                  <button data-testid={`team-delete-${t.id.slice(0, 8)}`} onClick={() => removeTeam(t)} aria-label="Delete team" className="p-2 text-white/60 hover:text-red-400 transition-colors duration-300"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-steel border border-white/10">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <p className="font-heading font-semibold uppercase tracking-wider text-white">All Employees</p>
          <p className="font-mono text-xs text-smoke">{members.length} accounts</p>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="employees-table" className="w-full min-w-[720px]">
            <thead>
              <tr className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke text-left">
                <th className="py-3 px-5 font-medium">Name</th>
                <th className="py-3 px-5 font-medium">Email</th>
                <th className="py-3 px-5 font-medium">Role</th>
                <th className="py-3 px-5 font-medium">Team</th>
                <th className="py-3 px-5 font-medium">Created</th>
                <th className="py-3 px-5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 ? (
                <tr><td colSpan={6} className="py-6 px-5 font-body text-sm text-smoke">No employees yet — click Add Employee.</td></tr>
              ) : members.map((m) => (
                <tr key={m.id} data-testid={`employee-row-${m.id.slice(0, 8)}`} className="border-t border-white/10 hover:bg-ink/40 transition-colors duration-300">
                  <td className="py-3.5 px-5 font-heading font-semibold text-base uppercase tracking-wide text-white">{m.name}</td>
                  <td className="py-3.5 px-5 font-mono text-xs text-smoke">{m.email}</td>
                  <td className="py-3.5 px-5">
                    <span className="font-mono text-[10px] tracking-widest uppercase px-2 py-1 bg-yellow-600/30 text-yellow-300">SALES</span>
                  </td>
                  <td className="py-3.5 px-5">
                    <select
                      data-testid={`employee-team-${m.id.slice(0, 8)}`}
                      value={m.team_id || ""}
                      onChange={(e) => assignTeam(m, e.target.value)}
                      className="bg-ink border border-white/20 font-mono text-xs text-white/80 px-2 py-1.5 focus:outline-none"
                    >
                      <option value="">Default (fallback)</option>
                      {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </td>
                  <td className="py-3.5 px-5 font-mono text-xs text-smoke">{fmtDate(m.created_at)}</td>
                  <td className="py-3.5 px-5 text-right">
                    <button data-testid={`employee-delete-${m.id.slice(0, 8)}`} onClick={() => removeMember(m)} aria-label="Delete employee" className="p-2 text-white/50 hover:text-red-400 transition-colors duration-300"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {teamModal && (
        <div className="fixed inset-0 z-50 bg-ink/90 overflow-y-auto p-4 md:p-10" data-testid="team-modal">
          <div className="max-w-2xl mx-auto bg-steel border border-white/10 p-6 md:p-8 relative">
            <button data-testid="team-modal-close" onClick={() => setTeamModal(null)} aria-label="Close" className="absolute top-4 right-4 p-2 text-white/50 hover:text-white"><X size={20} /></button>
            <h2 className="font-heading font-semibold uppercase text-2xl tracking-wide text-white mb-6">{teamModal.id ? "Edit Team" : "New Team"}</h2>

            <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Team Name</label>
            <input
              data-testid="team-name-input"
              value={teamModal.name}
              onChange={(e) => setTeamModal({ ...teamModal, name: e.target.value })}
              placeholder="e.g., Junior Sales, Regional Manager, Read-only"
              className="mt-2 mb-5 w-full bg-ink border border-white/20 px-4 py-3 font-body text-sm text-white placeholder:text-white/30 focus:border-[#7FB3E8] focus:outline-none transition-colors duration-300"
            />

            <div className="flex items-center justify-between mb-4">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Tab-wise Permissions · {selected.length} Enabled</p>
              <div className="flex gap-2">
                <button data-testid="team-select-all" onClick={() => setTeamModal({ ...teamModal, permissions: allPerms })} className="font-mono text-[10px] tracking-widest uppercase text-[#7FB3E8] hover:text-white px-2 py-1 flex items-center gap-1"><Check size={12} /> Select All</button>
                <button data-testid="team-clear-all" onClick={() => setTeamModal({ ...teamModal, permissions: [] })} className="font-mono text-[10px] tracking-widest uppercase text-white/60 hover:text-white px-2 py-1">Clear All</button>
              </div>
            </div>

            <div className="flex flex-col gap-3 max-h-[45vh] overflow-y-auto pr-1" data-testid="team-permissions-list">
              {GROUP_ORDER.filter((g) => groups[g]).map((g) => {
                const perms = groups[g].map((a) => `${g}:${a}`);
                const on = perms.filter((p) => selected.includes(p)).length;
                return (
                  <div key={g} className="border border-white/10">
                    <button type="button" data-testid={`team-group-${g}`} onClick={() => toggleGroup(g)} className="w-full flex items-center justify-between bg-ink/60 px-4 py-2.5">
                      <span className="flex items-center gap-2.5 font-heading font-semibold uppercase tracking-wider text-sm text-white">
                        <span className={`inline-block w-3.5 h-3.5 border ${on === perms.length ? "bg-[#7FB3E8] border-[#7FB3E8]" : on > 0 ? "bg-[#7FB3E8]/40 border-[#7FB3E8]" : "border-white/30"}`} />
                        {GROUP_LABELS[g]}
                      </span>
                      <span className="font-mono text-xs text-smoke">{on} / {perms.length}</span>
                    </button>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 px-4 py-3">
                      {groups[g].map((a) => {
                        const p = `${g}:${a}`;
                        const checked = selected.includes(p);
                        return (
                          <label key={p} data-testid={`team-perm-${g}-${a}`} className="flex items-center gap-2.5 cursor-pointer font-body text-sm text-white/80 hover:text-white">
                            <input type="checkbox" checked={checked} onChange={() => togglePerm(p)} className="accent-[#7FB3E8] w-4 h-4" />
                            {ACTION_LABELS[a]}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="mt-4 font-body text-xs text-smoke">Sales users assigned to this team will only see the tabs &amp; actions ticked above. Admins always keep full access.</p>
            <div className="flex justify-end gap-3 mt-6">
              <button data-testid="team-cancel-button" onClick={() => setTeamModal(null)} className="font-heading font-semibold uppercase tracking-wider text-sm px-5 py-2.5 border border-white/20 text-white/70 hover:text-white transition-colors duration-300">Cancel</button>
              <button data-testid="team-save-button" onClick={saveTeam} disabled={saving} className="btn-sweep btn-navy font-heading font-semibold uppercase tracking-wider text-sm px-6 py-2.5 flex items-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 size={15} className="animate-spin" /> : "Save Team"}
              </button>
            </div>
          </div>
        </div>
      )}

      {empModal && (
        <div className="fixed inset-0 z-50 bg-ink/90 overflow-y-auto p-4 md:p-10" data-testid="employee-modal">
          <div className="max-w-md mx-auto bg-steel border border-white/10 p-6 md:p-8 relative">
            <button data-testid="employee-modal-close" onClick={() => setEmpModal(false)} aria-label="Close" className="absolute top-4 right-4 p-2 text-white/50 hover:text-white"><X size={20} /></button>
            <h2 className="font-heading font-semibold uppercase text-2xl tracking-wide text-white mb-6">Add Employee</h2>
            <div className="flex flex-col gap-4">
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Name</label>
                <input data-testid="employee-name-input" value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} className="mt-1 w-full bg-ink border border-white/20 px-4 py-2.5 font-body text-sm text-white focus:border-[#7FB3E8] focus:outline-none" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Email</label>
                <input data-testid="employee-email-input" type="email" value={empForm.email} onChange={(e) => setEmpForm({ ...empForm, email: e.target.value })} className="mt-1 w-full bg-ink border border-white/20 px-4 py-2.5 font-body text-sm text-white focus:border-[#7FB3E8] focus:outline-none" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Password (min 8 chars)</label>
                <input data-testid="employee-password-input" type="password" minLength={8} value={empForm.password} onChange={(e) => setEmpForm({ ...empForm, password: e.target.value })} className="mt-1 w-full bg-ink border border-white/20 px-4 py-2.5 font-body text-sm text-white focus:border-[#7FB3E8] focus:outline-none" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Team</label>
                <select data-testid="employee-team-select" value={empForm.team_id} onChange={(e) => setEmpForm({ ...empForm, team_id: e.target.value })} className="mt-1 w-full bg-ink border border-white/20 font-mono text-sm text-white px-3 py-2.5 focus:outline-none">
                  <option value="">Default (fallback)</option>
                  {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <p className="font-body text-xs text-smoke">Role is SALES — access comes from the assigned team. They log in at the same /admin page.</p>
              <div className="flex justify-end gap-3 mt-2">
                <button data-testid="employee-cancel-button" onClick={() => setEmpModal(false)} className="font-heading font-semibold uppercase tracking-wider text-sm px-5 py-2.5 border border-white/20 text-white/70 hover:text-white transition-colors duration-300">Cancel</button>
                <button data-testid="employee-save-button" onClick={saveEmployee} disabled={saving} className="btn-sweep btn-navy font-heading font-semibold uppercase tracking-wider text-sm px-6 py-2.5 flex items-center gap-2 disabled:opacity-60">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : "Add Employee"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
